// ─────────────────────────────────────────────────────────────────────────────
// Imports
// ─────────────────────────────────────────────────────────────────────────────
import { generateTimestampedName } from '..';
import { IBotData, IBotSection, IBotTunnel, IIdNameValue } from '../../models';

// ─────────────────────────────────────────────────────────────────────────────
// Constants & Keys
// ─────────────────────────────────────────────────────────────────────────────
export const TAB_STORAGE_KEY = 'bot_active_tab_v1';
export const MAX_SECTIONS_PER_TUNNEL = 20;
export const BOT_FORM_STORAGE_KEY = 'bot_form_state_v1';
export const PLENUM_LENGTH = 30;
export const GENERATED_TAG_REGEX = /^AH-\d{6}_\d{6}$/;
// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────
const createId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export const isGeneratedTag = (tag?: string | null): boolean => {
  if (!tag) return false;
  return GENERATED_TAG_REGEX.test(tag);
};

// ─────────────────────────────────────────────────────────────────────────────
// Default Section Definitions
// ─────────────────────────────────────────────────────────────────────────────
export const sectionBaseDefaults: Pick<
  IBotSection,
  'type' | 'accessories' | 'name'
> = {
  type: 'plenum',
  name: 'Plenum',
  accessories: [],
};

export const RESET_FIELDS: (keyof IBotSection)[] = [
  'depth_nominal_id',
  'merv_id',
  'type_id',
  'conn_hand_id',
  'duty_id',
  'htfluid_id',
  'wide',
  'high',
  'airflow',
  'tsp',
  'mfg_id',
  'series_id',
  'rotor_style_id',
];

export const sectionDimensionDefaults: Pick<
  IBotSection,
  'wide' | 'high' | 'airflow' | 'tsp'
> = {
  wide: 1,
  high: 1,
  airflow: 10000,
  tsp: 3,
};

export const filterDefaults: Partial<IBotSection> = {
  depth_nominal_id: 2,
  merv_id: 8,
  type_id: 1,
  frame_loading_id: 1,
  air_stream_id: 1,
};

export const fanDefaults: Partial<IBotSection> = {
  type_id: 1,
  air_stream_id: 1,
};

export const coilDefaults: Partial<IBotSection> = {
  type_id: 1,
  mfg_id: 79,
  duty_id: 1,
  htfluid_id: 1,
  htfluid: 'water',
  conn_hand_id: 2,
  air_stream_id: 1,
};

export const erWheelDefaults: Partial<IBotSection> = {
  mfg_id: undefined,
  series_id: undefined,
  type_id: undefined,
  rotor_style_id: undefined,
};

// Used by applyTypeDefaults()
const TYPE_DEFAULTS_MAP = {
  filter: filterDefaults,
  fan: fanDefaults,
  coil: coilDefaults,
  er_wheel: erWheelDefaults,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Tunnel Defaults
// ─────────────────────────────────────────────────────────────────────────────
export const tunnelDefaults: Pick<
  IBotTunnel,
  'index' | 'tunnel_index' | 'sections' | 'air_stream_id' | 'airflow'
> = {
  index: 'Tunnel 1',
  tunnel_index: '1:1',
  sections: [],
  air_stream_id: 1,
  airflow: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
// Factories (Section, Tunnel, Initial Data)
// ─────────────────────────────────────────────────────────────────────────────
export const createBotSection = (
  overrides: Partial<IBotSection> = {},
): IBotSection => ({
  id: overrides.id ?? createId('section'),
  type: overrides.type ?? sectionBaseDefaults.type,
  name: overrides.name ?? sectionBaseDefaults.name,
  accessories: [...(overrides.accessories ?? sectionBaseDefaults.accessories)],
  airflow: overrides.airflow as number,
  air_stream_id: overrides.air_stream_id,
  mfg_id: overrides.mfg_id,
});

export const createBotTunnel = (
  overrides: Partial<IBotTunnel> = {},
): IBotTunnel => {
  const sourceSections = overrides.sections ?? tunnelDefaults.sections;

  return {
    id: overrides.id ?? createId('tunnel'),
    index: overrides.index ?? tunnelDefaults.index,
    tunnel_index: overrides.tunnel_index ?? tunnelDefaults.tunnel_index,
    sections: sourceSections.map((s) => createBotSection(s)),
    air_stream_id: overrides.air_stream_id as number,
    airflow: overrides.airflow as number,
  };
};

export const createBotInitialData = (): IBotData => ({
  tag: generateTimestampedName(),
  construction_class_id: 1,
  floor_construction_id: 1,
  weather_rating_id: 1,
  voltage_id: 1,
  voltage_name: '460/3/60',
  sa_ea_intake_configuration: 'sa5_t1_single_supply',
  air_system_id: 5,
  tunnel_configuration: 1,
  sa_volume_max: 10000,
  tunnels: [
    createBotTunnel({
      index: 'Tunnel 1',
      tunnel_index: '1:1',
      air_stream_id: 1,
      airflow: 10000,
    }),
  ],
});

export const getStoredBotData = (): IBotData | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BOT_FORM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IBotData;
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

export const bot_initial_data = createBotInitialData();

export const checkIsMixedAirPlenum = (
  sectionIndex: number,
  air_system_id: number,
) => {
  return sectionIndex === 0 && air_system_id === 2;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tunnel Configurations
// ─────────────────────────────────────────────────────────────────────────────
import {
  BotConfigSelectOption,
  BotSectionOption,
  BotAccessoryOption,
  OptionLike,
} from '../../models/bot';

export const bot_tunnel_configurations: BotConfigSelectOption[] = [
  { label: 'single tunnel (1:1)', value: 1, id: 1, index: '1:1' },
  { label: 'Dual-tunnel over-under (1:2)', value: 2, id: 2, index: '1:2' },
  { label: 'Dual-tunnel side-by-side (2:1)', value: 9, id: 3, index: '2:1' },
];

export const bot_tunnel_configurations_all: BotConfigSelectOption[] = [
  ...bot_tunnel_configurations,
  {
    label:
      'Dual-tunnel over-under (1:2), with a counter-flow rotary heat-exchanger',
    value: 2,
    id: 4,
  },
  {
    label:
      'Dual-tunnel side-by-side (2:1), with a counter-flow rotary heat-exchanger',
    value: 9,
    id: 5,
  },
];

export const bot_tunnel_configuration_ids = {
  1: 1,
  2: 2,
  3: 9,
  4: 2,
  5: 9,
};
// ─────────────────────────────────────────────────────────────────────────────
// SA Configurations
// ─────────────────────────────────────────────────────────────────────────────
// Structure: bot_sa_configurations[air_system_id][tunnel_configuration]
// Each configuration can have:
//   - sa_ea_configurations: Array of options with label/value
//   - conditions: Optional top-level conditions (for backward compatibility)
//   - Each option in sa_ea_configurations can have its own conditions property
//     (for configurations where different options need different per-tunnel rules)
export const bot_sa_configurations = {
  /** =======================
   *  100% OA (air_system_id: 5)
   *  ======================= */
  5: {
    // Tunnel config 1 — single tunnel
    1: {
      sa_ea_configurations: [
        {
          label: 'Supply air flowing in a single direction',
          value: 'sa5_t1_single_supply',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1, // Supply
            airflow_direction_id: 1,
          },
        },
      ],
    },

    // Tunnel config 2 — dual over-under (1:2)
    2: {
      sa_ea_configurations: [
        {
          label:
            'Supply air (intake) in top tunnel, supply air (intake) in bottom tunnel, redundant, flowing in the same direction',
          value: 'sa5_t2_top_bottom_supply_same',
          conditions: {
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 1, fan_count: 1 }, // bottom
              { airflow_direction_id: 1, fan_count: 1 }, // top
            ],
          },
        },
        {
          label:
            'Supply air (intake) in bottom tunnel, supply air (discharge) in top tunnel, flowing in opposite directions',
          value: 'sa5_t2_bottom_top_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 1 }, // bottom
              { airflow_direction_id: 2 }, // top
            ],
          },
        },
        {
          label:
            'Supply air (intake) top tunnel, supply air (discharge) in bottom tunnel, flowing in opposite directions',
          value: 'sa5_t2_top_bottom_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 2 }, // bottom
              { airflow_direction_id: 1 }, // top
            ],
          },
        },
      ],
    },
    // Tunnel config 3 — dual side-by-side (2:1)
    3: {
      sa_ea_configurations: [
        {
          label:
            'Supply air (intake) in the right tunnel, supply air (intake) in the left, redundant, lowing in the same direction',
          value: 'sa5_t3_right_left_supply_same',
          conditions: {
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 1, fan_count: 1 }, // left
              { airflow_direction_id: 1, fan_count: 1 }, // right
            ],
          },
        },
        {
          label:
            'Supply air (intake) in the right tunnel, supply air (discharge) in the left tunnel, flowing in opposite directions',
          value: 'sa5_t3_right_left_opposite_same',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 1 }, // right
              { airflow_direction_id: 2 }, // left
            ],
          },
        },
        {
          label:
            'Supply air (intake) in the left tunnel, supply air (discharge) in the right tunnel, flowing in opposite directions',
          value: 'sa5_t3_left_right_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 2 }, // right
              { airflow_direction_id: 1 }, // left
            ],
          },
        },
      ],
    },
    4: {
      sa_ea_configurations: [
        {
          label:
            'Supply air at the bottom, exhaust air at the top, flowing in opposite directions',
          value: 'sa5_t4_bottom_supply_top_exhaust',
          conditions: {
            rotary_htex: true,
            per_tunnel: [
              {
                fan_count: 1,
                required_fan_count: 1,
                air_stream_id: 1,
                airflow_direction_id: 1,
              }, // bottom
              {
                fan_count: 1,
                air_stream_id: 4,
                airflow_direction_id: 2,
                required_fan_count: 1,
              }, // top
            ],
          },
        },
        {
          label:
            'Supply air at the top, exhaust air at the bottom, flowing in opposite directions',
          value: 'sa5_t4_top_supply_bottom_exhaust',
          conditions: {
            rotary_htex: true,
            per_tunnel: [
              {
                fan_count: 1,
                air_stream_id: 4,
                airflow_direction_id: 2,
                required_fan_count: 1,
              }, // bottom
              {
                fan_count: 1,
                air_stream_id: 1,
                airflow_direction_id: 1,
                required_fan_count: 1,
              }, // top
            ],
          },
        },
      ],
    },
    5: {
      sa_ea_configurations: [
        {
          label:
            'Supply air on the right, exhaust air on the left, flowing in opposite directions',
          value: 'sa5_t5_right_supply_left_exhaust',
          conditions: {
            rotary_htex: true,
            per_tunnel: [
              {
                fan_count: 1,
                air_stream_id: 4,
                airflow_direction_id: 2,
                required_fan_count: 1,
              }, // left
              {
                fan_count: 1,
                air_stream_id: 1,
                airflow_direction_id: 1,
                required_fan_count: 1,
              }, // right
            ],
          },
        },
        {
          label:
            'Supply air on the left, exhaust air on the right, flowing in opposite directions',
          value: 'sa5_t5_left_supply_right_exhaust',
          conditions: {
            rotary_htex: true,
            per_tunnel: [
              {
                fan_count: 1,
                air_stream_id: 1,
                airflow_direction_id: 2,
                required_fan_count: 1,
              }, // right
              {
                fan_count: 1,
                air_stream_id: 4,
                airflow_direction_id: 1,
                required_fan_count: 1,
              }, // left
            ],
          },
        },
      ],
    },
  },

  /** =======================
   *  100% RA (air_system_id: 4)
   *  ======================= */
  4: {
    // Tunnel config 1
    1: {
      sa_ea_configurations: [
        {
          label: 'Return air flowing in a single direction',
          value: 'sa4_t1_single_return',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2, // Return
            airflow_direction_id: 1,
          },
        },
      ],
    },

    // Tunnel config 2 — over-under
    2: {
      sa_ea_configurations: [
        {
          label:
            'Return air (intake) in top tunnel, return air (intake) in bottom tunnel, flowing in the same direction',
          value: 'sa4_t2_top_bottom_return_same',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 1 }, // bottom
              { airflow_direction_id: 1 }, // top
            ],
          },
        },
        {
          label:
            'Return air (intake) in bottom tunnel, return air (discharge) in top tunnel, flowing in opposite directions',
          value: 'sa4_t2_bottom_top_return_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 1 }, // bottom
              { airflow_direction_id: 2 }, // top
            ],
          },
        },
        {
          label:
            'Return air (intake) in top tunnel, return air (discharge) in bottom tunnel, flowing in opposite directions',
          value: 'sa4_t2_top_bottom_return_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 2 }, // bottom
              { airflow_direction_id: 1 }, // top
            ],
          },
        },
      ],
    },

    // Tunnel config 3 — side-by-side
    3: {
      sa_ea_configurations: [
        {
          label:
            'Return air (intake) in the right tunnel, return air (intake) in the left tunnel, flowing in the same direction',
          value: 'sa4_t3_right_left_return_same',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 1 }, // right
              { airflow_direction_id: 1 }, // left
            ],
          },
        },
        {
          label:
            'Return air (intake) in the right tunnel, return air (discharge) in the left tunnel, flowing in opposite directions',
          value: 'sa4_t3_right_left_return_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 1 }, // right
              { airflow_direction_id: 2 }, // left
            ],
          },
        },
        {
          label:
            'Return air (intake) in the left tunnel, return air (discharge) in the right tunnel, flowing in opposite directions',
          value: 'sa4_t3_left_right_return_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 2,
            per_tunnel: [
              { airflow_direction_id: 2 }, // right
              { airflow_direction_id: 1 }, // left
            ],
          },
        },
      ],
    },
  },

  /** =======================
   *  100% EA (air_system_id: 3)
   *  ======================= */
  3: {
    // Tunnel config 1
    1: {
      sa_ea_configurations: [
        {
          label: 'Exhaust air flowing in a single direction',
          value: 'sa3_t1_single_exhaust',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4, // Exhaust
            airflow_direction_id: 1,
          },
        },
      ],
    },

    // Tunnel config 2 — rule 10
    2: {
      sa_ea_configurations: [
        {
          label:
            'Exhaust air (intake) at the top, exhaust air (intake) at the bottom, flowing in the same direction',
          value: 'sa3_t2_top_bottom_exhaust_same',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 1 }, // bottom
              { airflow_direction_id: 1 }, // top
            ],
          },
        },
        {
          label:
            'Exhaust air (intake) at the bottom, exhaust air (discharge) at the top, flowing in opposite directions',
          value: 'sa3_t2_bottom_top_exhaust_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 1 }, // bottom
              { airflow_direction_id: 2 }, // top
            ],
          },
        },
        {
          label:
            'Exhaust air (intake) at the top, exhaust air (discharge) at the bottom, flowing in opposite directions',
          value: 'sa3_t2_top_bottom_exhaust_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 2 }, // bottom
              { airflow_direction_id: 1 }, // top
            ],
          },
        },
      ],
    },

    // Tunnel config 3 — rule 11
    3: {
      sa_ea_configurations: [
        {
          label:
            'Exhaust air (intake) on the right, exhaust air (intake) on the left, flowing in the same direction',
          value: 'sa3_t3_right_left_exhaust_same',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 1 }, // right
              { airflow_direction_id: 1 }, // left
            ],
          },
        },
        {
          label:
            'Exhaust air (intake) on the right, exhaust air (discharge) on the left, flowing in opposite directions',
          value: 'sa3_t3_right_left_exhaust_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 1 }, // right
              { airflow_direction_id: 2 }, // left
            ],
          },
        },
        {
          label:
            'Exhaust air (intake) on the left, exhaust air (discharge) on the right flowing in opposite directions',
          value: 'sa3_t3_left_right_exhaust_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 4,
            per_tunnel: [
              { airflow_direction_id: 2 }, // right
              { airflow_direction_id: 1 }, // left
            ],
          },
        },
      ],
    },
  },

  /** =======================
   *  Mixed Air (air_system_id: 2)
   *  ======================= */
  2: {
    /** Tunnel configuration 1
     *  Single tunnel (1:1)
     */
    1: {
      sa_ea_configurations: [
        {
          label: 'Supply air flowing in a single direction',
          value: 'sa2_t1_single_supply',
          conditions: {
            max_fan_sections_in_ahu: 1,
            per_tunnel: [
              {
                air_stream_id: 1, // Supply
                airflow_direction_id: 1,
                add_outside_airflow: true,
                add_return_airflow: true,
              },
            ],
          },
        },
      ],
    },

    /** Tunnel configuration 2
     *  Dual-tunnel over-under (1:2)
     */
    2: {
      sa_ea_configurations: [
        {
          label:
            'Outside air & return air (intake) in bottom tunnel, supply air (discharge) in top tunnel, flowing in opposite directions',
          value: 'sa2_t2_bottom_top_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              {
                airflow_direction_id: 1,
                add_return_airflow: true,
                add_outside_airflow: true,
              }, // bottom
              { airflow_direction_id: 2 }, // top
            ],
          },
        },
        {
          label:
            'Outside air & return air (intake) at top tunnel, supply air (discharge) in bottom tunnel, flowing in opposite directions',
          value: 'sa2_t2_top_bottom_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 2 }, // bottom
              {
                airflow_direction_id: 1,
                add_return_airflow: true,
                add_outside_airflow: true,
              }, // top
            ],
          },
        },
      ],
    },

    /** Tunnel configuration 3
     *  Dual-tunnel side-by-side (2:1)
     */
    3: {
      sa_ea_configurations: [
        {
          label:
            'Outside air & return air (intake) in the right tunnel, supply air (discharge) in the left tunnel, flowing in opposite directions',
          value: 'sa2_t3_right_left_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              {
                airflow_direction_id: 1,
                add_return_airflow: true,
                add_outside_airflow: true,
              }, // right
              { airflow_direction_id: 2 }, // left
            ],
          },
        },
        {
          label:
            'Outside air & return (intake) in the left tunnel, supply air (discharge) in the right tunnel, flowing in opposite directions',
          value: 'sa2_t3_left_right_supply_opposite',
          conditions: {
            max_fan_sections_in_ahu: 1,
            air_stream_id: 1,
            per_tunnel: [
              { airflow_direction_id: 2 }, // right
              {
                airflow_direction_id: 1,
                add_return_airflow: true,
                add_outside_airflow: true,
              }, // left
            ],
          },
        },
      ],
    },
  },
  /** =======================
   * Economizer (air_system_id: 2)
   *  ======================= */
  1: {
    /** Tunnel configuration 1
     *  Single tunnel (1:1)
     */
    1: {
      sa_ea_configurations: [],
    },

    /** Tunnel configuration 2
     *  Dual-tunnel over-under (1:2)
     */
    2: {
      sa_ea_configurations: [],
    },

    /** Tunnel configuration 3
     *  Dual-tunnel side-by-side (2:1)
     */
    3: {
      sa_ea_configurations: [],
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// UI Tabs
// ─────────────────────────────────────────────────────────────────────────────
export const bot_tabs = [
  { id: 'form', label: 'Form' },
  { id: 'free-form', label: 'Free Form' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Section Types
// ─────────────────────────────────────────────────────────────────────────────

export const bot_section_types: BotSectionOption[] = [
  { label: 'Plenum', value: 'plenum' },
  { label: 'Filter', value: 'filter' },
  { label: 'Coil', value: 'coil' },
  { label: 'Fan', value: 'fan' },
];

export const bot_fan_types: IIdNameValue[] = [
  { id: 1, name: 'Plenum', value: 'plenum' },
  { id: 2, name: 'Plug', value: 'plug' },
];

export const bot_coil_types: IIdNameValue[] = [
  { name: 'Fluid', id: 1, value: 'fluid' },
  { name: 'Evaporator', id: 2, value: 'evaporator' },
  { name: 'Steam', id: 3, value: 'steam' },
];

export const filter_mervs: { [key: string | number]: number[] } = {
  1: [8],
  2: [8],
  4: [8],
  12: [13, 14, 15],
  15: [13, 14, 15],
  22: [13, 14, 15],
  30: [13, 14, 15],
};

export const filter_types: { [key: string | number]: number[] } = {
  1: [1],
  2: [1],
  4: [1],
  12: [2, 3, 4, 5],
  15: [5],
  22: [5],
  30: [5],
};
// ─────────────────────────────────────────────────────────────────────────────
// Accessory Types (Added)
// ─────────────────────────────────────────────────────────────────────────────

export const accessoryTypes: BotAccessoryOption[] = [
  { label: 'Access door', value: 'access_door' },
  { label: 'Opening', value: 'opening' },
  // { label: 'Damper', value: 'damper' },
  { label: 'Drain Pan', value: 'drain_pan' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Default Application Helpers
// ─────────────────────────────────────────────────────────────────────────────
export const applyCommonDefaults = (target: IBotSection) => {
  Object.assign(target, {
    wide: target.wide ?? sectionDimensionDefaults.wide,
    high: target.high ?? sectionDimensionDefaults.high,
    airflow: target.airflow ?? sectionDimensionDefaults.airflow,
    tsp: target.tsp ?? sectionDimensionDefaults.tsp,
  });
};

export const applyTypeDefaults = (
  type: IBotSection['type'],
  target: IBotSection,
) => {
  const defaults = TYPE_DEFAULTS_MAP[type as keyof typeof TYPE_DEFAULTS_MAP];
  if (!defaults) return;

  Object.entries(defaults).forEach(([key, value]) => {
    if ((target as any)[key] === undefined) {
      (target as any)[key] = value;
    }
  });
};

export const resetBotFields = (target: IBotSection) => {
  RESET_FIELDS.forEach((key) => delete (target as any)[key]);
};

export const applyAccessDoorMounting = (
  airflow_direction_id = 1,
  tunnel_configuration = 1,
  tunnel_index = 0,
): number => {
  // Default mounting
  let mounting = 6;

  // Configuration 2 depends only on airflow direction
  if (tunnel_configuration === 2) {
    return airflow_direction_id === 2 ? 5 : 6;
  }

  // Configuration 9 depends on airflow direction AND tunnel index
  if (tunnel_configuration === 9) {
    const isFirstTunnel = !tunnel_index;
    if (
      (airflow_direction_id === 1 && isFirstTunnel) ||
      (airflow_direction_id === 2 && !isFirstTunnel)
    ) {
      return 6;
    }

    return 5;
  }

  return mounting;
};

// ─────────────────────────────────────────────────────────────────────────────
// Section Options Provider
// ─────────────────────────────────────────────────────────────────────────────
export const getBotSectionOptions = (
  botData: IBotData,
  tunnelIndex: number,
): BotSectionOption[] => {
  const base = bot_section_types;

  // Check if rotary_htex is enabled in the current configuration
  const airSystemId = botData.air_system_id ?? 1;
  const tunnelConfig = botData.tunnel_configuration ?? 1;
  const saEaConfig = botData.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  // Allow er_wheel if:
  // 1. rotary_htex is true in the conditions AND
  // 2. tunnelIndex is 0 (first tunnel only)
  const allowErWheel = rules?.rotary_htex === true && tunnelIndex === 0;

  return allowErWheel
    ? [...base, { value: 'er_wheel', label: 'Rotary Heat-Exchanger' }]
    : base;
};

// utils/summaryUtils.ts

export const stringify = (value: unknown) =>
  value === undefined || value === null ? undefined : String(value);

export const resolveOptionLabel = (option: OptionLike): string => {
  if (option === null || option === undefined) return '';
  if (typeof option === 'string' || typeof option === 'number')
    return String(option);

  const rec = option as Record<string, any>;
  return (
    rec.label ??
    rec.name ??
    stringify(rec.value) ??
    rec.spn ??
    rec.series ??
    rec.description ??
    ''
  );
};

export const getOptionLabel = (
  options: OptionLike[] | undefined | null,
  target?: number | string | null,
): string | undefined => {
  if (!options || target === undefined || target === null) return undefined;
  const targetStr = String(target);

  const match = options.find((option) => {
    if (option == null) return false;
    if (typeof option !== 'object') return String(option) === targetStr;

    const rec = option as Record<string, any>;
    const optionValue =
      rec.id ?? rec.value ?? rec.key ?? rec.code ?? rec.name ?? rec.label;

    return optionValue != null && String(optionValue) === targetStr;
  });

  return resolveOptionLabel(match);
};

export const formatSectionType = (type?: string) =>
  type
    ? type
        .split('_')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ')
    : '';

export const formatAirflowValue = (value?: number | null) =>
  typeof value === 'number' && !Number.isNaN(value)
    ? value.toLocaleString()
    : '-';

export const formatPressureValue = (value?: number | null) =>
  typeof value === 'number' && !Number.isNaN(value)
    ? Number.isInteger(value)
      ? value.toFixed(0)
      : value.toFixed(2)
    : '-';

export const toDisplayText = (value?: string | number | null) => {
  if (value == null) return '';
  const t = String(value).trim();
  return t.length ? t : '';
};

export const createRow = (
  label: string,
  value?: any,
  unit = '',
  indent = false,
) => ({
  label,
  value: toDisplayText(value),
  unit,
  indent,
});

// ─────────────────────────────────────────────────────────────────────────────
// Fan Section Management
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gets the SA/EA configuration options for a given air system and tunnel configuration
 */
export const getSAEAConfigurations = (
  air_system_id: number,
  tunnel_configuration: number,
): Array<{ label: string; value: string; conditions?: any }> => {
  const config = (bot_sa_configurations as any)[air_system_id]?.[
    tunnel_configuration
  ];
  return config?.sa_ea_configurations ?? [];
};

/**
 * Gets the fan section rules for the current configuration
 *
 * This function checks for conditions in the following order:
 * 1. Conditions specific to the selected sa_ea_intake_configuration option (if provided)
 * 2. Top-level conditions (for backward compatibility with older configurations)
 */
export const getAHURules = (
  air_system_id: number,
  tunnel_configuration: number,
  sa_ea_intake_configuration?: string,
): {
  max_fan_sections_in_ahu?: number;
  air_stream_id?: number;
  fan_count?: number;
  required_fan_count?: number;
  rotary_htex?: boolean;
  add_outside_airflow?: boolean;
  add_return_airflow?: boolean;
  per_tunnel?: Array<{
    fan_count?: number;
    required_fan_count?: number;
    air_stream_id?: number;
    airflow_direction_id?: number;
    add_return_airflow?: boolean;
    add_outside_airflow?: boolean;
  }>;
} | null => {
  const config = (bot_sa_configurations as any)[air_system_id]?.[
    tunnel_configuration
  ];
  if (!config) return null;

  // If sa_ea_intake_configuration is provided, try to find conditions from the specific option
  if (sa_ea_intake_configuration && config.sa_ea_configurations) {
    const option = config.sa_ea_configurations.find(
      (opt: any) => opt.value === sa_ea_intake_configuration,
    );
    if (option?.conditions) {
      return option.conditions;
    }
  }

  // Fall back to top-level conditions if available
  return config?.conditions ?? null;
};

/**
 * Applies fan section rules to the bot data
 * This function is idempotent and can be called multiple times safely
 */
export const applyFanSectionRules = (draft: IBotData): void => {
  const airSystemId = draft.air_system_id ?? 1;
  const tunnelConfig = draft.tunnel_configuration ?? 1;
  const saEaConfig = draft.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  if (!rules) return;

  // Collect all fan sections across all tunnels
  const allFanSections: Array<{
    tunnelIndex: number;
    sectionIndex: number;
    section: IBotSection;
  }> = [];
  const requiredAirStreamIds: any = [];
  draft.tunnels.forEach((tunnel, tIdx) => {
    requiredAirStreamIds.push(
      rules.per_tunnel?.[tIdx]?.air_stream_id || rules.air_stream_id,
    );
    tunnel.sections.forEach((section, sIdx) => {
      if (section.type === 'fan') {
        allFanSections.push({ tunnelIndex: tIdx, sectionIndex: sIdx, section });
      }
    });
  });

  // Case 1: max_fan_sections_in_ahu rule
  // This limits the TOTAL number of fan sections across ALL tunnels combined
  // e.g., if max_fan_sections_in_ahu is 1, only 1 fan section can exist total (in any tunnel)
  if (rules.max_fan_sections_in_ahu !== undefined) {
    const maxFans = rules.max_fan_sections_in_ahu;
    // Remove extra fan sections (keep only the first maxFans across all tunnels)
    if (allFanSections.length > maxFans) {
      // Sort by tunnel index, then section index to ensure consistent removal
      const sortedFans = [...allFanSections].sort((a, b) => {
        if (a.tunnelIndex !== b.tunnelIndex) {
          return a.tunnelIndex - b.tunnelIndex;
        }
        return a.sectionIndex - b.sectionIndex;
      });

      // Remove fans beyond the limit (from end to start to maintain indices)
      for (let i = sortedFans.length - 1; i >= maxFans; i--) {
        const { tunnelIndex, sectionIndex } = sortedFans[i];
        draft.tunnels[tunnelIndex]?.sections.splice(sectionIndex, 1);
      }
    }
    // Set air_stream_id and airflow for all remaining fan sections and their accessories
    draft.tunnels.forEach((tunnel, tIdx) => {
      tunnel.sections.forEach((section) => {
        const requiredAirStreamId = requiredAirStreamIds[tIdx];
        const fanAirflow = getAirflowFromAirStreamId(
          requiredAirStreamId,
          draft,
        );
        if (section.type === 'fan') {
          section.air_stream_id = requiredAirStreamId;
          section.airflow = fanAirflow;
          // Set air_stream_id for accessories in fan sections
          if (section.accessories && Array.isArray(section.accessories)) {
            section.accessories.forEach((accessory) => {
              accessory.air_stream_id = requiredAirStreamId;
            });
          }
        }
      });
    });
  }
  // Case 2: per_tunnel rule
  else if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    rules.per_tunnel.forEach((tunnelRule, ruleIndex) => {
      const tunnel = draft.tunnels[ruleIndex];
      if (!tunnel) return;
      const fanCount = tunnelRule.fan_count;
      const requiredFanCount = tunnelRule.required_fan_count || 0;
      const requiredAirStreamId = tunnelRule.air_stream_id;

      // Get current fan sections in this tunnel
      const tunnelFanSections: Array<{
        sectionIndex: number;
        section: IBotSection;
      }> = [];
      tunnel.sections.forEach((section, sIdx) => {
        if (section.type === 'fan') {
          tunnelFanSections.push({ sectionIndex: sIdx, section });
        }
      });

      const currentFanCount = tunnelFanSections.length;

      // Only process fan count rules if fanCount is defined
      if (fanCount !== undefined) {
        // Remove extra fans if we have more than required
        if (currentFanCount > fanCount) {
          // Remove from end to maintain indices (sort descending by sectionIndex)
          const sortedFans = [...tunnelFanSections].sort(
            (a, b) => b.sectionIndex - a.sectionIndex,
          );
          const fansToRemove = currentFanCount - fanCount;
          for (let i = 0; i < fansToRemove; i++) {
            const { sectionIndex } = sortedFans[i];
            tunnel.sections.splice(sectionIndex, 1);
          }
        }
        // Create missing fans if we have fewer than required
        if (currentFanCount < requiredFanCount) {
          const fansToCreate = requiredFanCount - currentFanCount;
          for (let i = 0; i < fansToCreate; i++) {
            const newFan = createBotSection({ type: 'fan', name: 'Fan' });
            applyTypeDefaults('fan', newFan);
            if (requiredAirStreamId !== undefined) {
              newFan.air_stream_id = requiredAirStreamId;
              newFan.airflow = getAirflowFromAirStreamId(
                requiredAirStreamId,
                draft,
              );
            }
            tunnel.sections.push(newFan);
          }
        }
      }

      // Set air_stream_id and airflow for all sections and accessories in this tunnel
      // Use per_tunnel air_stream_id if available, otherwise fall back to conditions.air_stream_id
      const airStreamIdToUse =
        requiredAirStreamId !== undefined
          ? requiredAirStreamId
          : rules.air_stream_id;

      if (airStreamIdToUse !== undefined) {
        const tunnelAirflow = getAirflowFromAirStreamId(
          airStreamIdToUse,
          draft,
        );
        tunnel.sections.forEach((section) => {
          // Set air_stream_id for all sections (not just fans)
          section.air_stream_id = airStreamIdToUse;
          // Set airflow for fan sections
          if (section.type === 'fan') {
            section.airflow = tunnelAirflow;
          }

          // Set air_stream_id for all accessories in this section
          if (section.accessories && Array.isArray(section.accessories)) {
            section.accessories.forEach((accessory) => {
              accessory.air_stream_id = airStreamIdToUse;
            });
          }
        });
      }
    });
  }
  // Case 3: fan_count at top level (applies to first tunnel for single tunnel configuration)
  else if (rules.fan_count !== undefined) {
    const fanCount = rules.fan_count;
    const requiredFanCount = rules.required_fan_count || 0;
    const requiredAirStreamId = rules.air_stream_id;

    // Get the first tunnel (for single tunnel configuration)
    const tunnel = draft.tunnels[0];
    if (!tunnel) return;

    // Get current fan sections in this tunnel
    const tunnelFanSections: Array<{
      sectionIndex: number;
      section: IBotSection;
    }> = [];
    tunnel.sections.forEach((section, sIdx) => {
      if (section.type === 'fan') {
        tunnelFanSections.push({ sectionIndex: sIdx, section });
      }
    });

    const currentFanCount = tunnelFanSections.length;

    // Remove extra fans if we have more than required
    if (currentFanCount > fanCount) {
      // Remove from end to maintain indices (sort descending by sectionIndex)
      const sortedFans = [...tunnelFanSections].sort(
        (a, b) => b.sectionIndex - a.sectionIndex,
      );
      const fansToRemove = currentFanCount - fanCount;
      for (let i = 0; i < fansToRemove; i++) {
        const { sectionIndex } = sortedFans[i];
        tunnel.sections.splice(sectionIndex, 1);
      }
    }
    // Create missing fans if we have fewer than required
    if (currentFanCount < requiredFanCount) {
      const fansToCreate = requiredFanCount - currentFanCount;
      const fanAirflow =
        requiredAirStreamId !== undefined
          ? getAirflowFromAirStreamId(requiredAirStreamId, draft)
          : undefined;
      for (let i = 0; i < fansToCreate; i++) {
        const newFan = createBotSection({ type: 'fan', name: 'Fan' });
        applyTypeDefaults('fan', newFan);
        if (requiredAirStreamId !== undefined) {
          newFan.air_stream_id = requiredAirStreamId;
          if (fanAirflow !== undefined) {
            newFan.airflow = fanAirflow;
          }
        }
        tunnel.sections.push(newFan);
      }
    }

    // Set air_stream_id and airflow for all fan sections and their accessories
    if (requiredAirStreamId !== undefined) {
      const fanAirflow = getAirflowFromAirStreamId(requiredAirStreamId, draft);
      tunnel.sections.forEach((section) => {
        if (section.type === 'fan') {
          section.air_stream_id = requiredAirStreamId;
          section.airflow = fanAirflow;
          // Set air_stream_id for accessories in fan sections
          if (section.accessories && Array.isArray(section.accessories)) {
            section.accessories.forEach((accessory) => {
              accessory.air_stream_id = requiredAirStreamId;
            });
          }
        }
      });
    }
  }
};

/**
 * Gets airflow value based on air_stream_id from draft volumes
 * air_stream_id mapping:
 *   1 (default) -> sa_volume_max
 *   2 -> ra_volume_max
 *   3 -> oa_volume_max (not available in IBotData, falls back to sa_volume_max)
 *   4 -> ea_volume_max
 */
const getAirflowFromAirStreamId = (
  airStreamId: number,
  draft: IBotData,
  fallbackAirflow?: number,
): number => {
  const defaultAirflow: number =
    fallbackAirflow ?? sectionDimensionDefaults.airflow ?? 10000;
  switch (airStreamId) {
    case 2:
      return +(draft.ra_volume_max ?? defaultAirflow) as number;
    case 4:
      return +(draft.ea_volume_max ?? defaultAirflow) as number;
    case 1:
    default:
      return +(draft.sa_volume_max ?? defaultAirflow) as number;
  }
};

/**
 * Applies section rules to all non-fan sections (filter, coil, plenum)
 * Sets airflow and air_stream_id for all filter, coil, and plenum sections based on configuration rules
 */
export const applyOtherSectionRules = (draft: IBotData): void => {
  const airSystemId = draft.air_system_id ?? 1;
  const tunnelConfig = draft.tunnel_configuration ?? 1;
  const saEaConfig = draft.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  if (!rules) return;
  // Section types to process (all except 'fan')
  const sectionTypes = ['filter', 'coil', 'plenum'] as const;

  // Check per_tunnel rules first
  if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    draft.tunnels.forEach((tunnel, tunnelIndex) => {
      const tunnelRule = rules.per_tunnel![tunnelIndex];
      const tunnelAirStreamId =
        tunnelRule?.air_stream_id ?? rules.air_stream_id;

      const tunnelAirflowDirectionId = tunnelRule?.airflow_direction_id ?? 1;

      if (tunnelAirStreamId !== undefined) {
        const tunnelAirflow = getAirflowFromAirStreamId(
          tunnelAirStreamId,
          draft,
        );

        // Set air_stream_id and airflow on tunnel
        tunnel.air_stream_id = tunnelAirStreamId;
        tunnel.airflow = tunnelAirflow;
        tunnel.airflow_direction_id = tunnelAirflowDirectionId;
        tunnel.sections.forEach((section) => {
          if (sectionTypes.includes(section.type as any)) {
            section.airflow = tunnelAirflow;
            section.air_stream_id = tunnelAirStreamId;

            // Set air_stream_id for accessories in sections
            if (section.accessories && Array.isArray(section.accessories)) {
              section.accessories.forEach((accessory) => {
                accessory.air_stream_id = tunnelAirStreamId;
              });
            }
          }
        });
      }
      if (rules.rotary_htex) {
        const hasErWheel = tunnel.sections.some(
          (section) => section.type === 'er_wheel',
        );
        if (!hasErWheel) {
          tunnel.sections.push(
            createBotSection({
              type: 'er_wheel',
              mfg_id: 233,
              name: 'Rotary Heat-Exchanger',
            }),
          );
        }
      } else {
        // Remove existing er_wheel sections
        tunnel.sections = tunnel.sections.filter(
          (section) => section.type !== 'er_wheel',
        );
      }
    });
  } else {
    // Use top-level air_stream_id
    const airStreamId = rules.air_stream_id;

    if (airStreamId !== undefined) {
      draft.tunnels.forEach((tunnel) => {
        const tunnelAirflow = getAirflowFromAirStreamId(airStreamId, draft);

        // Set air_stream_id and airflow on tunnel
        tunnel.air_stream_id = airStreamId;
        tunnel.airflow = tunnelAirflow;
        tunnel.air_stream_id = 1;

        tunnel.sections.forEach((section) => {
          if (sectionTypes.includes(section.type as any)) {
            section.airflow = tunnelAirflow;
            section.air_stream_id = airStreamId;

            // Set air_stream_id for accessories in sections
            if (section.accessories && Array.isArray(section.accessories)) {
              section.accessories.forEach((accessory) => {
                accessory.air_stream_id = airStreamId;
              });
            }
          }
        });
      });
    }
  }

  const first_tunnel_airflow_direction_id =
    draft.tunnels[0].airflow_direction_id;
  draft.tunnels_have_same_directions =
    draft.tunnels.length === 1 ||
    draft.tunnels.filter(
      (t) => t.airflow_direction_id === first_tunnel_airflow_direction_id,
    )?.length > 1;
};

/**
 * Applies add_return_airflow flag to tunnels based on sa_ea_intake_configuration
 * Sets add_return_airflow to true/false for each tunnel based on the configuration rules
 */
export const applyAddReturnAirflowRules = (draft: IBotData): void => {
  const airSystemId = draft.air_system_id ?? 1;
  const tunnelConfig = draft.tunnel_configuration ?? 1;
  const saEaConfig = draft.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);
  if (!rules) {
    // If no rules, set add_return_airflow to false for all tunnels
    draft.tunnels.forEach((tunnel) => {
      tunnel.add_return_airflow = false;
    });
    return;
  }

  // Check per_tunnel rules first
  if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    draft.tunnels.forEach((tunnel, tunnelIndex) => {
      const tunnelRule = rules.per_tunnel![tunnelIndex];
      // Set add_return_airflow based on the tunnel rule, default to false
      tunnel.add_return_airflow = tunnelRule?.add_return_airflow ?? false;
    });
  } else {
    // If no per_tunnel rules, set add_return_airflow to false for all tunnels
    draft.tunnels.forEach((tunnel) => {
      tunnel.add_return_airflow = !!rules.add_return_airflow;
    });
  }
};

/**
 * Applies add_outside_airflow flag to tunnels based on sa_ea_intake_configuration
 * Sets add_outside_airflow to true/false for each tunnel based on the configuration rules
 */
export const applyAddOutsideAirflowRules = (draft: IBotData): void => {
  const airSystemId = draft.air_system_id ?? 1;
  const tunnelConfig = draft.tunnel_configuration ?? 1;
  const saEaConfig = draft.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  if (!rules) {
    // If no rules, set add_outside_airflow to false for all tunnels
    draft.tunnels.forEach((tunnel) => {
      tunnel.add_outside_airflow = false;
    });
    return;
  }

  // Check per_tunnel rules first
  if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    draft.tunnels.forEach((tunnel, tunnelIndex) => {
      const tunnelRule = rules.per_tunnel![tunnelIndex];
      // Set add_outside_airflow based on the tunnel rule, default to false
      tunnel.add_outside_airflow = tunnelRule?.add_outside_airflow ?? false;
    });
  } else {
    // If no per_tunnel rules, check top-level add_outside_airflow
    const addOutsideAirflow = rules.add_outside_airflow ?? false;
    draft.tunnels.forEach((tunnel) => {
      tunnel.add_outside_airflow = addOutsideAirflow;
    });
  }
};

/**
 * Checks if a fan section can be added based on current rules
 */
export const canAddFanSection = (
  botData: IBotData,
  tunnelIndex: number,
): boolean => {
  const airSystemId = botData.air_system_id ?? 1;
  const tunnelConfig = botData.tunnel_configuration ?? 1;
  const saEaConfig = botData.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  if (!rules) return true; // No rules, allow adding

  // Case 1: max_fan_sections_in_ahu rule
  // This limits the TOTAL number of fan sections across ALL tunnels combined
  if (rules.max_fan_sections_in_ahu !== undefined) {
    const allFanSections = botData.tunnels.flatMap((tunnel) =>
      tunnel.sections.filter((s) => s.type === 'fan'),
    );
    return allFanSections.length < rules.max_fan_sections_in_ahu;
  }
  // Case 2: per_tunnel rule
  else if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    const tunnelRule = rules.per_tunnel[tunnelIndex];
    if (!tunnelRule) return true; // No rule for this tunnel

    const tunnel = botData.tunnels[tunnelIndex];
    if (!tunnel) return false;

    const currentFanCount = tunnel.sections.filter(
      (s) => s.type === 'fan',
    ).length;
    // If fan_count is not defined, allow adding (no restriction)
    if (tunnelRule.fan_count === undefined) return true;
    return currentFanCount < tunnelRule.fan_count;
  }
  // Case 3: fan_count at top level (applies to first tunnel)
  else if (rules.fan_count !== undefined) {
    // Only applies to the first tunnel (index 0)
    if (tunnelIndex !== 0) return false;

    const tunnel = botData.tunnels[0];
    if (!tunnel) return false;

    const currentFanCount = tunnel.sections.filter(
      (s) => s.type === 'fan',
    ).length;
    return currentFanCount < rules.fan_count;
  }

  return true;
};

/**
 * Checks if a fan section can be removed based on current rules
 */
export const canRemoveFanSection = (
  botData: IBotData,
  tunnelIndex: number,
  sectionIndex: number,
): boolean => {
  const tunnel = botData.tunnels[tunnelIndex];
  if (!tunnel) return false;

  const section = tunnel.sections[sectionIndex];
  if (!section || section.type !== 'fan') return true; // Not a fan, allow removing
  return (
    botData.tunnel_configuration !== 4 && botData.tunnel_configuration !== 5
  );
  // const airSystemId = botData.air_system_id ?? 1;
  // const tunnelConfig = botData.tunnel_configuration ?? 1;
  // const saEaConfig = botData.sa_ea_intake_configuration;
  // const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  // if (!rules) return true; // No rules, allow removing

  // const tunnel = botData.tunnels[tunnelIndex];
  // if (!tunnel) return false;

  // const section = tunnel.sections[sectionIndex];
  // if (!section || section.type !== 'fan') return true; // Not a fan, allow removing

  // // Case 1: max_fan_sections_in_ahu rule - can remove if we have more than 0
  // if (rules.max_fan_sections_in_ahu !== undefined) {
  //   const allFanSections = botData.tunnels.flatMap((tunnel) =>
  //     tunnel.sections.filter((s) => s.type === 'fan'),
  //   );
  //   return allFanSections.length > 0;
  // }
  // // Case 2: per_tunnel rule - cannot remove if at required count
  // else if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
  //   const tunnelRule = rules.per_tunnel[tunnelIndex];
  //   if (!tunnelRule) return true; // No rule for this tunnel

  //   const currentFanCount = tunnel.sections.filter(
  //     (s) => s.type === 'fan',
  //   ).length;
  //   // If fan_count is not defined, allow removing (no restriction)
  //   if (tunnelRule.fan_count === undefined) return true;
  //   return currentFanCount > tunnelRule.fan_count;
  // }
  // // Case 3: fan_count at top level - cannot remove if at required count
  // else if (rules.fan_count !== undefined) {
  //   // Only applies to the first tunnel (index 0)
  //   if (tunnelIndex !== 0) return true; // Can remove from other tunnels

  //   const currentFanCount = tunnel.sections.filter(
  //     (s) => s.type === 'fan',
  //   ).length;
  //   return currentFanCount > rules.fan_count;
  // }

  // return true;
};

/**
 * Checks if a section type can be changed to 'fan' based on current rules
 */
export const canChangeToFan = (
  botData: IBotData,
  tunnelIndex: number,
): boolean => {
  return canAddFanSection(botData, tunnelIndex);
};

/**
 * Checks if the 'fan' option should be shown in the section type dropdown
 * Returns false if the fan limit has been reached
 */
export const shouldShowFanOption = (
  botData: IBotData,
  tunnelIndex: number,
  currentSectionId?: string,
): boolean => {
  const airSystemId = botData.air_system_id ?? 1;
  const tunnelConfig = botData.tunnel_configuration ?? 1;
  const saEaConfig = botData.sa_ea_intake_configuration;
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

  if (!rules) return true; // No rules, allow showing fan option

  // Case 1: max_fan_sections_in_ahu rule
  if (rules.max_fan_sections_in_ahu !== undefined) {
    const allFanSections = botData.tunnels.flatMap((tunnel) =>
      tunnel.sections.filter((s) => s.type === 'fan'),
    );
    // If current section is already a fan, always show the option (so user can see it's selected)
    if (currentSectionId) {
      const currentSection = botData.tunnels
        .flatMap((t) => t.sections)
        .find((s) => s.id === currentSectionId);
      if (currentSection?.type === 'fan') {
        return true;
      }
    }
    return allFanSections.length < rules.max_fan_sections_in_ahu;
  }
  // Case 2: per_tunnel rule
  else if (rules.per_tunnel && Array.isArray(rules.per_tunnel)) {
    const tunnelRule = rules.per_tunnel[tunnelIndex];
    if (!tunnelRule) return true; // No rule for this tunnel

    const tunnel = botData.tunnels[tunnelIndex];
    if (!tunnel) return false;

    const currentFanCount = tunnel.sections.filter(
      (s) => s.type === 'fan',
    ).length;
    // If current section is already a fan, always show the option
    if (currentSectionId) {
      const currentSection = tunnel.sections.find(
        (s) => s.id === currentSectionId,
      );
      if (currentSection?.type === 'fan') {
        return true;
      }
    }
    // If fan_count is not defined, show the option (no restriction)
    if (tunnelRule.fan_count === undefined) return true;
    return currentFanCount < tunnelRule.fan_count;
  }
  // Case 3: fan_count at top level (applies to first tunnel)
  else if (rules.fan_count !== undefined) {
    // Only applies to the first tunnel (index 0)
    if (tunnelIndex !== 0) return false;

    const tunnel = botData.tunnels[0];
    if (!tunnel) return false;

    const currentFanCount = tunnel.sections.filter(
      (s) => s.type === 'fan',
    ).length;
    // If current section is already a fan, always show the option
    if (currentSectionId) {
      const currentSection = tunnel.sections.find(
        (s) => s.id === currentSectionId,
      );
      if (currentSection?.type === 'fan') {
        return true;
      }
    }
    return currentFanCount < rules.fan_count;
  }

  return true;
};

export const getConnectionHandId = (
  tunnel_configuration: number,
  tunnelIndex: number,
  airflow_direction_id?: number,
) => {
  const id =
    bot_tunnel_configuration_ids[tunnel_configuration as 1 | 2 | 3 | 4 | 5];
  let value = 2;

  if (id === 9) {
    if (
      (tunnelIndex && airflow_direction_id === 1) ||
      (!tunnelIndex && airflow_direction_id === 2)
    )
      value = 1;
  }
  return {
    enable: id !== 9,
    value,
  };
};
